/**
 * Message Component
 * Displays a single chat message (user or assistant)
 */

import { memo, useMemo, useState } from 'react';
import {
  User,
  Bot,
  Clock,
  AlertTriangle,
  AlertCircle,
  XCircle,
  Wifi,
  Shield,
  Zap,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { cn } from '@/utils/classNames';
import { formatMessageTime } from '@/utils/formatters';
import { selectDisplayedCitations, groupByLabel } from '@/utils/citationDisplay';
import { usePreferencesSelector } from '@/context/usePreferencesSelector';
import { sendFeedback } from '@/services/feedbackTelemetry';
import type { MessageFeedback } from '@/types';

// CWE-80 mitigation: Veracode does not recognize custom helpers as XSS
// cleansers, so the href value is validated INLINE against a literal
// `https://` prefix and a strict character whitelist. Anything else falls
// back to `'#'`, which is inert when clicked.
const HTTPS_URL_PATTERN = /^https:\/\/[A-Za-z0-9._~:/?#[\]@!$&'()*+,;=%-]+$/;
import { MarkdownContent } from './MarkdownContent';
import type { Message as MessageType } from '@/types';

export interface MessageProps {
  message: MessageType;
  showAvatar?: boolean;
  isGrouped?: boolean;
  /**
   * Conversation that contains this message. Forwarded to the
   * /api/feedback-events beacon so the dashboard can roll feedback up
   * by conversation.
   */
  conversationId?: string | null;
  /**
   * Agent that produced this message. Forwarded to the feedback beacon
   * for the per-agent feedback grid on the dashboard's Quality page.
   */
  agentId?: string | null;
  /**
   * Deployment / model name for the assistant response. Forwarded to
   * the feedback beacon so the dashboard can correlate quality with
   * model.
   */
  model?: string | null;
  /**
   * Called whenever the user toggles a thumbs rating (or clears it).
   * The parent should persist the new feedback state on the message via
   * the storage adapter so it survives reloads. Telemetry is fired
   * inside the component regardless of whether this callback is wired.
   */
  onFeedbackChange?: (
    messageId: string,
    feedback: MessageFeedback | null
  ) => void;
}

/**
 * Get icon component for warning type
 */
function getWarningIcon(type: string): JSX.Element {
  switch (type) {
    case 'truncated':
      return <AlertTriangle className="h-4 w-4 flex-shrink-0" />;
    case 'content_filter':
      return <Shield className="h-4 w-4 flex-shrink-0" />;
    case 'rate_limit':
      return <Zap className="h-4 w-4 flex-shrink-0" />;
    case 'auth_error':
      return <XCircle className="h-4 w-4 flex-shrink-0" />;
    case 'service_error':
      return <AlertCircle className="h-4 w-4 flex-shrink-0" />;
    case 'timeout':
    case 'network_error':
      return <Wifi className="h-4 w-4 flex-shrink-0" />;
    default:
      return <AlertCircle className="h-4 w-4 flex-shrink-0" />;
  }
}

/**
 * Get color classes for warning type
 */
function getWarningColors(type: string): {
  bg: string;
  text: string;
  border: string;
} {
  switch (type) {
    case 'truncated':
      return {
        bg: 'bg-warning-light/10 dark:bg-warning-dark/10',
        text: 'text-warning',
        border: 'border-warning/20',
      };
    case 'content_filter':
      return {
        bg: 'bg-error-light/10 dark:bg-error-dark/10',
        text: 'text-error',
        border: 'border-error/20',
      };
    case 'rate_limit':
      return {
        bg: 'bg-info-light/10 dark:bg-info-dark/10',
        text: 'text-info',
        border: 'border-info/20',
      };
    case 'auth_error':
      return {
        bg: 'bg-error-light/10 dark:bg-error-dark/10',
        text: 'text-error',
        border: 'border-error/20',
      };
    case 'service_error':
    case 'timeout':
    case 'network_error':
      return {
        bg: 'bg-warning-light/10 dark:bg-warning-dark/10',
        text: 'text-warning',
        border: 'border-warning/20',
      };
    default:
      return {
        bg: 'bg-bg-tertiary',
        text: 'text-text-secondary',
        border: 'border-border-default',
      };
  }
}

function MessageImpl({
  message,
  showAvatar = true,
  isGrouped = false,
  conversationId = null,
  agentId = null,
  model = null,
  onFeedbackChange,
}: MessageProps) {
  const showTimestamps = usePreferencesSelector((p) => p.showTimestamps);
  const enableCitations = usePreferencesSelector((p) => p.enableCitations);
  const showTokenUsage = usePreferencesSelector((p) => p.showTokenUsage);
  const messageGrouping = usePreferencesSelector((p) => p.messageGrouping);
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const isAssistant = message.role === 'assistant';
  const currentFeedback: MessageFeedback | null = message.feedback ?? null;
  const [showCommentBox, setShowCommentBox] = useState<boolean>(false);
  const [commentDraft, setCommentDraft] = useState<string>('');
  const [commentSent, setCommentSent] = useState<boolean>(false);

  // Pre-filter citations once per message: keep only the sources the model
  // actually cited inline (so uncited retrieval results don't clutter the
  // list), label each with its matching inline marker ([Web 3] etc.), keep
  // the inline regex check (Veracode requires the literal pattern inline),
  // and pair each citation with its resolved safe URL.
  const safeCitations = useMemo(() => {
    if (!message.citations) return [];
    const displayed = selectDisplayedCitations(
      message.content ?? '',
      message.citations
    );
    return displayed.map(({ citation, label }) => {
      const rawUrl =
        typeof citation.webUrl === 'string' ? citation.webUrl.trim() : '';
      const safeUrl =
        rawUrl.startsWith('https://') && HTTPS_URL_PATTERN.test(rawUrl)
          ? rawUrl
          : '#';
      return { citation, safeUrl, label };
    });
  }, [message.citations, message.content]);

  // Group the displayed citations by source (Web / AI Search / M365) so the
  // Sources list reads in tidy sections instead of one undifferentiated run.
  const citationGroups = useMemo(
    () => groupByLabel(safeCitations),
    [safeCitations]
  );

  const handleFeedbackClick = (rating: MessageFeedback): void => {
    // Toggle off when clicking the already-active rating.
    if (currentFeedback === rating) {
      onFeedbackChange?.(message.id, null);
      setShowCommentBox(false);
      setCommentDraft('');
      setCommentSent(false);
      return;
    }

    onFeedbackChange?.(message.id, rating);
    setCommentSent(false);
    setCommentDraft('');
    // Reveal the optional comment box on both 👍 and 👎. Comments are
    // valuable signal regardless of direction — admins want to see what
    // worked, not just what failed.
    setShowCommentBox(true);

    void sendFeedback({
      messageId: message.id,
      conversationId,
      agentId,
      rating,
      model,
    });
  };

  const handleCommentSubmit = (): void => {
    const trimmed = commentDraft.trim();
    // Type-narrow `currentFeedback` for the sendFeedback `rating` field.
    // The comment box is only mounted after a rating click which sets
    // currentFeedback to a non-null value (toggle-off closes the box),
    // so this guard normally only filters the empty-comment case.
    if (!trimmed || !currentFeedback) return;
    void sendFeedback({
      messageId: message.id,
      conversationId,
      agentId,
      rating: currentFeedback,
      comment: trimmed,
      model,
    });
    setCommentSent(true);
    setShowCommentBox(false);
  };

  // Determine if avatar should be shown based on grouping preference
  const shouldShowAvatar =
    showAvatar && (!isGrouped || !messageGrouping);

  // System messages have special styling
  if (isSystem) {
    return (
      <div className="mx-auto my-4 max-w-md">
        <div className="flex items-center justify-center gap-2 rounded-lg border border-border-default bg-message-system-bg px-4 py-2">
          <Clock className="h-4 w-4 text-text-tertiary" />
          <p className="text-center text-sm italic text-message-system-text">
            {message.content}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex gap-3',
        isUser && 'flex-row-reverse'
      )}
    >
      {/* Avatar */}
      {shouldShowAvatar && (
        <div
          className={cn(
            'flex h-8 w-8 flex-shrink-0 items-center',
            'justify-center rounded-full',
            isUser
              ? 'bg-stellar-primary text-white'
              : 'bg-bg-tertiary text-text-secondary'
          )}
        >
          {isUser ? (
            <User className="h-4 w-4" />
          ) : (
            <Bot className="h-4 w-4" />
          )}
        </div>
      )}

      {/* Message bubble */}
      <div
        className={cn(
          // min-w-0 lets this flex item shrink below its content's
          // intrinsic width. Without it, a wide code block (or any
          // unbreakable content) inflates the bubble's min-content size
          // past the max-width cap and the whole bubble overflows the
          // chat column — instead, min-w-0 keeps the bubble within the
          // column and lets the code block's own overflow-x-auto scroll.
          'message-bubble flex min-w-0 flex-col',
          isUser ? 'message-sent' : 'message-received'
        )}
      >
        {/* Security flag banner — non-blocking, informational only.
            Shown above the assistant response when the security scan
            endpoint flagged a high-severity match on the originating
            user prompt.  We never reject or rewrite the prompt; the
            banner just lets users + admins know an audit-log row was
            recorded. */}
        {isAssistant && message.securityFlag && (
          <div
            data-testid="security-flag-banner"
            role="status"
            className={cn(
              'mb-2 flex items-start gap-2 rounded-lg p-2',
              'bg-warning/10 text-warning border border-warning/20'
            )}
          >
            <Shield className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden />
            <p className="text-xs">
              This message triggered a security flag — your admin can review.
            </p>
          </div>
        )}

        {/* Content */}
        {isUser ? (
          <p className="whitespace-pre-wrap text-base leading-relaxed">
            {message.content}
          </p>
        ) : (
          <MarkdownContent content={message.content} />
        )}

        {/* Attached image previews (vision input). Only images carry a
            data-URL in `content`; document attachments are not rendered. */}
        {message.files && message.files.some((f) => f.type === 'image') && (
          <div className="mt-2 flex flex-wrap gap-2">
            {message.files
              .filter((f) => f.type === 'image' && f.content)
              .map((f) => (
                <img
                  key={f.id}
                  src={f.content}
                  alt={f.name}
                  className="max-h-48 max-w-[12rem] rounded-lg border border-border-default object-contain"
                />
              ))}
          </div>
        )}

        {/* Timestamp and status */}
        {(showTimestamps || message.status) && (
          <div
            className={cn(
              'mt-1 flex items-center gap-2',
              isUser ? 'justify-end' : 'justify-start'
            )}
          >
            {showTimestamps && (
              <time
                className={cn(
                  'text-xs',
                  isUser
                    ? 'text-message-sent-timestamp'
                    : 'text-message-received-timestamp'
                )}
              >
                {formatMessageTime(message.timestamp)}
              </time>
            )}

            {/* Status indicator for user messages */}
            {isUser && message.status && (
              <MessageStatus status={message.status} />
            )}
          </div>
        )}

        {/* Citations (if present and enabled). Grouped by source and laid
            out as wrapping rows so many sources stay compact vertically. */}
        {enableCitations && safeCitations.length > 0 && (
          <div className="mt-3 border-t border-border-default pt-3">
            <p className="mb-2 text-xs font-semibold text-text-secondary">
              Sources:
            </p>
            <div className="space-y-1.5">
              {citationGroups.map((group) => (
                <div
                  key={group.key}
                  className="flex flex-wrap items-baseline gap-x-3 gap-y-1"
                >
                  <span className="shrink-0 text-xs font-semibold text-text-tertiary">
                    {group.heading}
                  </span>
                  {group.items.map(({ citation, safeUrl, label }) => (
                    <a
                      key={citation.id}
                      href={safeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-stellar-primary hover:underline"
                    >
                      [{label}] {citation.documentName}
                    </a>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Warning metadata (if present) */}
        {message.warning && (
          <div className="mt-3 border-t border-border-default pt-3">
            <div
              className={cn(
                'rounded-lg border p-3',
                getWarningColors(message.warning.type).bg,
                getWarningColors(message.warning.type).border
              )}
            >
              {/* Icon + Title + Message */}
              <div className="flex items-start gap-2 mb-2">
                <div className={getWarningColors(message.warning.type).text}>
                  {getWarningIcon(message.warning.type)}
                </div>
                <div className="flex-1">
                  <p
                    className={cn(
                      'text-sm font-semibold',
                      getWarningColors(message.warning.type).text
                    )}
                  >
                    {message.warning.title}
                  </p>
                  <p className="text-xs text-text-secondary mt-1">
                    {message.warning.message}
                  </p>
                </div>
              </div>

              {/* Actionable tips */}
              {message.warning.actionable &&
                message.warning.actionable.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs text-text-secondary">
                    {message.warning.actionable.map((tip, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-text-tertiary">•</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                )}

              {/* Technical details (collapsible) */}
              {message.warning.details && (
                <details className="mt-2">
                  <summary
                    className={cn(
                      'text-xs cursor-pointer',
                      'text-text-tertiary hover:text-text-secondary',
                      'transition-colors'
                    )}
                  >
                    Technical details
                  </summary>
                  <p className="mt-1 text-xs text-text-tertiary font-mono">
                    {message.warning.details}
                  </p>
                </details>
              )}
            </div>
          </div>
        )}

        {/* Token usage (if enabled and present) */}
        {showTokenUsage && message.tokenUsage && (
          <div className="mt-3 border-t border-border-default pt-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-text-tertiary">Token Usage:</span>
              <div className="flex items-center gap-4 font-mono">
                <span className="text-text-secondary">
                  Prompt: {message.tokenUsage.promptTokens.toLocaleString()}
                </span>
                <span className="text-text-secondary">
                  Completion:{' '}
                  {message.tokenUsage.completionTokens.toLocaleString()}
                </span>
                <span className="text-text-primary font-semibold">
                  Total: {message.tokenUsage.totalTokens.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Feedback (👍 / 👎) — assistant messages only */}
        {isAssistant && (
          <div className="mt-2 flex flex-col gap-2" data-testid="message-feedback">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => handleFeedbackClick('up')}
                aria-label="Mark this response as helpful"
                aria-pressed={currentFeedback === 'up'}
                className={cn(
                  'p-1 rounded-md transition-colors',
                  'hover:bg-bg-tertiary',
                  currentFeedback === 'up'
                    ? 'text-stellar-primary'
                    : 'text-text-tertiary'
                )}
              >
                <ThumbsUp
                  size={14}
                  fill={currentFeedback === 'up' ? 'currentColor' : 'none'}
                />
              </button>
              <button
                type="button"
                onClick={() => handleFeedbackClick('down')}
                aria-label="Mark this response as not helpful"
                aria-pressed={currentFeedback === 'down'}
                className={cn(
                  'p-1 rounded-md transition-colors',
                  'hover:bg-bg-tertiary',
                  currentFeedback === 'down'
                    ? 'text-stellar-primary'
                    : 'text-text-tertiary'
                )}
              >
                <ThumbsDown
                  size={14}
                  fill={currentFeedback === 'down' ? 'currentColor' : 'none'}
                />
              </button>
              {commentSent && (
                <span className="text-xs text-text-tertiary ml-2">
                  Thanks — feedback recorded.
                </span>
              )}
            </div>

            {showCommentBox && (
              <div className="flex flex-col gap-2">
                <textarea
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  maxLength={256}
                  placeholder="Add a comment (optional)"
                  aria-label="Feedback comment"
                  rows={2}
                  className={cn(
                    'w-full rounded-md border border-border-default',
                    'bg-bg-primary px-2 py-1 text-xs text-text-primary',
                    'focus:outline-none focus:ring-1 focus:ring-stellar-primary'
                  )}
                />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCommentSubmit}
                    disabled={commentDraft.trim().length === 0}
                    className={cn(
                      'px-2 py-1 rounded-md text-xs font-semibold',
                      'bg-stellar-primary text-white',
                      'hover:bg-stellar-primary-dark',
                      'disabled:opacity-50 disabled:cursor-not-allowed'
                    )}
                  >
                    Send
                  </button>
                  <span className="text-xs text-text-tertiary">
                    {commentDraft.length}/256
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function arePropsEqual(
  prev: Readonly<MessageProps>,
  next: Readonly<MessageProps>
): boolean {
  return (
    prev.message === next.message &&
    prev.showAvatar === next.showAvatar &&
    prev.isGrouped === next.isGrouped &&
    prev.conversationId === next.conversationId &&
    prev.agentId === next.agentId &&
    prev.model === next.model &&
    prev.onFeedbackChange === next.onFeedbackChange
  );
}

export const Message = memo(MessageImpl, arePropsEqual);

function MessageStatus({ status }: { status: string }) {
  const icons: Record<string, JSX.Element | null> = {
    sending: (
      <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/70 border-t-transparent" />
    ),
    sent: (
      <svg className="h-3 w-3 text-white/70" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    delivered: (
      <svg className="h-3 w-3 text-white/70" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13l4 4L23 7" />
      </svg>
    ),
    error: (
      <svg className="h-3 w-3 text-error-light" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
  };

  return icons[status] || null;
}
